import { useState } from "react";

import { C, CSM_LIST } from "../../../../constants";
import { uid } from "../../../../utils";

function validate(schema,values){
  const err={};
  for(const[f,r]of Object.entries(schema)){
    if(r.when&&!r.when(values))continue;
    const v=typeof values[f]==="string"?values[f].trim():values[f];
    if(r.required&&(v===undefined||v===null||v===""))err[f]=`${r.label} is required`;
    else if(v&&r.minLength&&String(v).length<r.minLength)err[f]=`${r.label} min ${r.minLength} chars`;
    else if(v!==""&&v!==undefined&&r.min!==undefined&&Number(v)<r.min)err[f]=`${r.label} min ${r.min}`;
    else if(v&&r.pattern&&!r.pattern.test(String(v)))err[f]=r.patternMsg||`${r.label} format invalid`;
  }
  return err;
}
const isValid=e=>Object.keys(e).length===0;
function FField({label,required,error,hint,children}){return <div className="fgroup"><label className="flabel">{label}{required&&<span className="req">*</span>}</label>{children}{error&&<span className="ferr">⚠ {error}</span>}{!error&&hint&&<span className="fhint">{hint}</span>}</div>;}
function FInput({name,value,onChange,onBlur,type="text",placeholder,cls="",min,step}){return <input className={`fctrl${cls?" "+cls:""}`} type={type} value={value} placeholder={placeholder} min={min} step={step} onChange={e=>onChange(name,e.target.value)} onBlur={onBlur?()=>onBlur(name):undefined}/>;}
function FSelect({name,value,onChange,onBlur,options,placeholder,cls=""}){return <select className={`fsel-ctrl${cls?" "+cls:""}`} value={value} onChange={e=>onChange(name,e.target.value)} onBlur={onBlur?()=>onBlur(name):undefined}>{placeholder&&<option value="">{placeholder}</option>}{options.map(o=><option key={typeof o==="string"?o:o.value} value={typeof o==="string"?o:o.value}>{typeof o==="string"?o:o.label}</option>)}</select>;}
const emptyRes=()=>({id:uid(),title:"",capabilities:"",hourlyCost:"",quantity:"",notes:""});
const CL_SCHEMA={
  clientName:    {required:true,minLength:2,label:"Client Name"},
  csm:           {required:true,label:"CSM"},
  contactPerson: {required:true,label:"Primary Contact"},
  email:         {required:true,label:"Contact Email",pattern:/.+@.+\..+/,patternMsg:"Email format invalid"},
  priority:      {required:true,label:"Priority"},
};

const CLIENT_PRIORITY_OPTIONS=[
  {value:"STANDARD",label:"Standard"},
  {value:"STRATEGIC",label:"Strategic"},
  {value:"VIP",label:"VIP"},
  {value:"ESCALATED",label:"Escalated"},
];
const PJ_SCHEMA={
  projectName:      {required:true,minLength:3,label:"Project Name"},
  // Client linkage — either pick an existing client (clientId) OR create a new
  // one inline. The `when` guards switch which field is required based on
  // the `createNewClient` toggle.
  clientId:         {required:true,label:"Client",when:v=>!v.createNewClient},
  newClientName:    {required:true,minLength:2,label:"Client Name",when:v=>v.createNewClient},
  newClientCsm:     {required:true,label:"Client CSM",when:v=>v.createNewClient},
  newClientContact: {required:true,label:"Client Contact",when:v=>v.createNewClient},
  newClientEmail:   {required:true,label:"Client Email",pattern:/.+@.+\..+/,patternMsg:"Email format invalid",when:v=>v.createNewClient},
  contractStartDate:{required:true,label:"Start Date"},
  contractEndDate:  {required:true,label:"End Date"},
  deliveryDeadline: {required:true,label:"Deadline"},
  billingModel:     {required:true,label:"Billing Model"},
  fixedFeeAmount:   {required:true,label:"Fixed Fee",min:1,when:v=>v.billingModel==="Fixed Fee"},
  // Tracker-critical fields — mandatory on every manual create.
  budgetHours:      {required:true,min:1,label:"Budget Hours"},
  hourlyRate:       {required:true,min:1,label:"Hourly Rate"},
  currency:         {required:true,label:"Currency"},
  billingFrequency: {required:true,label:"Billing Frequency"},
  priority:         {required:true,label:"Priority"},
  projectType:      {required:true,label:"Project Type"},
};

const CURRENCY_OPTIONS=[{value:"SEK",label:"SEK"},{value:"USD",label:"USD"},{value:"EUR",label:"EUR"},{value:"GBP",label:"GBP"}];
const BILLING_FREQ_OPTIONS=[{value:"monthly",label:"Monthly"},{value:"quarterly",label:"Quarterly"},{value:"annual",label:"Annual"},{value:"one_time",label:"One-time"}];
const PRIORITY_OPTIONS=[{value:"low",label:"Low"},{value:"medium",label:"Medium"},{value:"high",label:"High"},{value:"critical",label:"Critical"}];
const PROJECT_TYPE_OPTIONS=[{value:"development",label:"Development"},{value:"value_scan",label:"Value Scan"},{value:"growth",label:"Growth"},{value:"support",label:"Support"}];

export function CreateClientForm({onSubmit,onCancel}){
  const [v,setV]=useState({clientName:"",csm:"",contactPerson:"",email:"",priority:"STANDARD"});
  const [touched,setT]=useState({}); const [sub,setSub]=useState(false);
  const err=validate(CL_SCHEMA,v); const can=isValid(err)&&!sub;
  const set=(n,val)=>setV(p=>({...p,[n]:val})); const touch=n=>setT(t=>({...t,[n]:true})); const ve=n=>touched[n]?err[n]:undefined;
  async function go(){
    setT(Object.fromEntries(Object.keys(CL_SCHEMA).map(k=>[k,true])));
    if(!isValid(err))return;
    setSub(true);
    onSubmit({
      clientId:uid(),
      clientName:v.clientName.trim(),
      csm:v.csm,
      contactPerson:v.contactPerson.trim(),
      email:v.email.trim(),
      priority:v.priority,
    });
  }
  return (
    <div className="fp fade">
      <div className="det-back" onClick={onCancel}>← Cancel</div>
      <div className="pg-title" style={{marginBottom:4}}>Create Client</div>
      <p className="pg-sub" style={{marginBottom:24}}>Add a new client account</p>
      <div className="fcard">
        <div className="fcard-title">Client Information</div>
        <div className="frow2">
          <FField label="Client Name" required error={ve("clientName")}><FInput name="clientName" value={v.clientName} onChange={set} onBlur={touch} cls={ve("clientName")?"err":""} placeholder="e.g. Meridian Health"/></FField>
          <FField label="Client Success Manager" required error={ve("csm")}><FSelect name="csm" value={v.csm} onChange={set} onBlur={touch} cls={ve("csm")?"err":""} placeholder="— Select CSM —" options={CSM_LIST}/></FField>
        </div>
        <div className="frow2">
          <FField label="Primary Contact" required error={ve("contactPerson")}><FInput name="contactPerson" value={v.contactPerson} onChange={set} onBlur={touch} cls={ve("contactPerson")?"err":""} placeholder="e.g. Jane Doe"/></FField>
          <FField label="Contact Email" required error={ve("email")}><FInput name="email" type="email" value={v.email} onChange={set} onBlur={touch} cls={ve("email")?"err":""} placeholder="jane@client.com"/></FField>
        </div>
        <div className="frow2">
          <FField label="Priority" required error={ve("priority")}><FSelect name="priority" value={v.priority} onChange={set} onBlur={touch} cls={ve("priority")?"err":""} options={CLIENT_PRIORITY_OPTIONS}/></FField>
          <div/>
        </div>
      </div>
      <div className="form-actions"><button className="btn-pri" onClick={go} disabled={!can}>{sub?"Creating…":"Create Client"}</button><button className="btn-sec" onClick={onCancel}>Cancel</button><span style={{fontSize:11.5,color:C.dim,marginLeft:"auto"}}>Required fields <span style={{color:C.red}}>*</span></span></div>
    </div>
  );
}

export function CreateProjectForm({clients,onSubmit,onCancel, currentUser}){
  const CURRENT_USER = currentUser;
  // clientId (the DB FK) is what the backend needs. clientName is carried
  // alongside for display convenience only. When createNewClient is true,
  // the Client dropdown is swapped for inline client-creation fields and
  // the submit handler creates the Client first before the Project.
  const [v,setV]=useState({
    projectName:"", clientId:"", clientName:"",
    createNewClient:false,
    newClientName:"", newClientCsm:"", newClientContact:"", newClientEmail:"",
    contractStartDate:"", contractEndDate:"", deliveryDeadline:"",
    billingModel:"T&M", fixedFeeAmount:"",
    budgetHours:"", hourlyRate:"",
    currency:"SEK", billingFrequency:"monthly",
    priority:"medium", projectType:"development",
  });
  const [touched,setT]=useState({}); const [res,setRes]=useState([emptyRes()]); const [sub,setSub]=useState(false);
  const err=validate(PJ_SCHEMA,v); const resErr=v.billingModel==="T&M"&&res.some(r=>!r.title.trim()||!r.quantity); const can=isValid(err)&&!resErr&&!sub;
  const set=(n,val)=>{setV(p=>({...p,[n]:val}));setT(t=>({...t,[n]:true}));}; const touch=n=>setT(t=>({...t,[n]:true})); const ve=n=>touched[n]?err[n]:undefined;
  // When the user picks a client, resolve both id + name together so the
  // rest of the form sees a consistent pair.
  const pickClient=(id)=>{const c=clients.find(cl=>(cl.clientId||cl.id)===id); setV(p=>({...p, clientId:id, clientName:c?.clientName||c?.name||""})); setT(t=>({...t,clientId:true,clientName:true}));};
  const toggleNewClient=()=>setV(p=>({...p, createNewClient:!p.createNewClient, clientId:"", clientName:""}));
  const upRes=(id,f,val)=>setRes(rs=>rs.map(r=>r.id===id?{...r,[f]:val}:r));
  async function go(){
    setT(Object.fromEntries(Object.keys(PJ_SCHEMA).map(k=>[k,true])));
    if(!isValid(err)||resErr)return;
    setSub(true);
    onSubmit({
      projectId:uid(),
      projectName:v.projectName.trim(),
      // Client linkage — inline-created or existing
      createNewClient:v.createNewClient,
      newClient: v.createNewClient ? {
        name: v.newClientName.trim(),
        csm: v.newClientCsm,
        contactPerson: v.newClientContact.trim(),
        email: v.newClientEmail.trim(),
      } : null,
      clientId:v.clientId,
      clientName:v.clientName||v.newClientName.trim(),
      // Timeline
      contractStartDate:v.contractStartDate,
      contractEndDate:v.contractEndDate,
      deliveryDeadline:v.deliveryDeadline,
      // Billing model (internal T&M / Fixed Fee concept)
      billingModel:v.billingModel,
      fixedFeeAmount:v.billingModel==="Fixed Fee"?Number(v.fixedFeeAmount):null,
      // Tracker-critical fields
      budgetHours:Number(v.budgetHours),
      ratePerHour:Number(v.hourlyRate),
      currency:v.currency,
      billingFrequency:v.billingFrequency,
      priority:v.priority,
      projectType:v.projectType,
      // Remaining metadata carried forward as before
      projectStatus:"On Track",
      billables:0,
      NPS:null,
      weeklyReportStatus:"Pending",
      utilizationPct:0,
      profitPct:0,
      projectOwner:CURRENT_USER.name,
      assignedConsultants:[],
      lastClientCheckin:null,
      renewalProbability:50,
      riskNotes:"",
      resourceRequirements:res,
    });
  }
  const isTM=v.billingModel==="T&M";
  return (
    <div className="fp fade">
      <div className="det-back" onClick={onCancel}>← Cancel</div>
      <div className="pg-title" style={{marginBottom:4}}>Create Project</div>
      <p className="pg-sub" style={{marginBottom:24}}>Register a new delivery project</p>
      <div className="fcard">
        <div className="fcard-title">Project Details</div>
        <div className="frow1"><FField label="Project Name" required error={ve("projectName")}><FInput name="projectName" value={v.projectName} onChange={set} onBlur={touch} cls={ve("projectName")?"err":""} placeholder="e.g. Data Platform Modernisation"/></FField></div>
        {!v.createNewClient ? (
          <div className="frow2">
            <FField label="Client" required error={ve("clientId")}>
              <FSelect name="clientId" value={v.clientId} onChange={(n,val)=>pickClient(val)} onBlur={touch} cls={ve("clientId")?"err":""} placeholder="— Select client —" options={clients.map(c=>({value:c.clientId||c.id,label:c.clientName||c.name}))}/>
            </FField>
            <div style={{alignSelf:"end",paddingBottom:2}}>
              <button type="button" className="add-res" onClick={toggleNewClient}>+ Create new client inline</button>
            </div>
          </div>
        ) : (
          <>
            <div className="frow2">
              <FField label="Client Name" required error={ve("newClientName")}><FInput name="newClientName" value={v.newClientName} onChange={set} onBlur={touch} cls={ve("newClientName")?"err":""} placeholder="e.g. Meridian Health"/></FField>
              <FField label="Client Success Manager" required error={ve("newClientCsm")}><FSelect name="newClientCsm" value={v.newClientCsm} onChange={set} onBlur={touch} cls={ve("newClientCsm")?"err":""} placeholder="— Select CSM —" options={CSM_LIST}/></FField>
            </div>
            <div className="frow2">
              <FField label="Primary Contact" required error={ve("newClientContact")}><FInput name="newClientContact" value={v.newClientContact} onChange={set} onBlur={touch} cls={ve("newClientContact")?"err":""} placeholder="e.g. Jane Doe"/></FField>
              <FField label="Contact Email" required error={ve("newClientEmail")}><FInput name="newClientEmail" type="email" value={v.newClientEmail} onChange={set} onBlur={touch} cls={ve("newClientEmail")?"err":""} placeholder="jane@client.com"/></FField>
            </div>
            <div className="frow1"><button type="button" className="add-res" onClick={toggleNewClient}>← Use existing client</button></div>
          </>
        )}
      </div>
      <div className="fcard">
        <div className="fcard-title">Timeline &amp; Dates</div>
        <div className="frow2">
          <FField label="Contract Start" required error={ve("contractStartDate")}><FInput name="contractStartDate" type="date" value={v.contractStartDate} onChange={set} onBlur={touch} cls={ve("contractStartDate")?"err":""}/></FField>
          <FField label="Contract End"   required error={ve("contractEndDate")}><FInput name="contractEndDate"   type="date" value={v.contractEndDate}   onChange={set} onBlur={touch} cls={ve("contractEndDate")?"err":""}/></FField>
        </div>
        <div className="frow2"><FField label="Delivery Deadline" required error={ve("deliveryDeadline")} hint="Internal milestone"><FInput name="deliveryDeadline" type="date" value={v.deliveryDeadline} onChange={set} onBlur={touch} cls={ve("deliveryDeadline")?"err":""}/></FField><div/></div>
      </div>
      <div className="fcard">
        <div className="fcard-title">Tracker &amp; Financial</div>
        <p className="pg-sub" style={{marginTop:-4,marginBottom:16,fontSize:12}}>Required for push to tracker — drives revenue, utilization, and capacity statistics.</p>
        <div className="frow2">
          <FField label="Budget Hours" required error={ve("budgetHours")} hint="Estimated total engagement hours"><FInput name="budgetHours" type="number" min="1" step="1" value={v.budgetHours} onChange={set} onBlur={touch} cls={`mono${ve("budgetHours")?" err":""}`} placeholder="e.g. 320"/></FField>
          <FField label="Hourly Rate" required error={ve("hourlyRate")} hint={`Billing rate in ${v.currency}`}><FInput name="hourlyRate" type="number" min="1" step="1" value={v.hourlyRate} onChange={set} onBlur={touch} cls={`mono${ve("hourlyRate")?" err":""}`} placeholder="e.g. 1250"/></FField>
        </div>
        <div className="frow2">
          <FField label="Currency" required error={ve("currency")}><FSelect name="currency" value={v.currency} onChange={set} onBlur={touch} cls={ve("currency")?"err":""} options={CURRENCY_OPTIONS}/></FField>
          <FField label="Billing Frequency" required error={ve("billingFrequency")}><FSelect name="billingFrequency" value={v.billingFrequency} onChange={set} onBlur={touch} cls={ve("billingFrequency")?"err":""} options={BILLING_FREQ_OPTIONS}/></FField>
        </div>
        <div className="frow2">
          <FField label="Priority" required error={ve("priority")}><FSelect name="priority" value={v.priority} onChange={set} onBlur={touch} cls={ve("priority")?"err":""} options={PRIORITY_OPTIONS}/></FField>
          <FField label="Project Type" required error={ve("projectType")}><FSelect name="projectType" value={v.projectType} onChange={set} onBlur={touch} cls={ve("projectType")?"err":""} options={PROJECT_TYPE_OPTIONS}/></FField>
        </div>
      </div>
      <div className="fcard">
        <div className="fcard-title">Billing Model</div>
        <div className="frow1" style={{marginBottom:16}}><FField label="Billing Model" required><div className="btog">{["T&M","Fixed Fee"].map(o=><button key={o} className={`btog-opt${v.billingModel===o?" sel":""}`} onClick={()=>set("billingModel",o)}>{o}</button>)}</div></FField></div>
        {!isTM&&<div className="frow2"><FField label={`Fixed Fee Amount (${v.currency})`} required error={ve("fixedFeeAmount")}><FInput name="fixedFeeAmount" type="number" value={v.fixedFeeAmount} onChange={set} onBlur={touch} placeholder="0" min="1" cls={`mono${ve("fixedFeeAmount")?" err":""}`}/></FField><div/></div>}
        <div style={{fontSize:"11px",fontWeight:700,color:C.sub,textTransform:"uppercase",letterSpacing:".1em",marginBottom:10}}>{isTM?"Resource Requirements":"Internal Resource Planning (Optional)"}</div>
        <div className="res-rows">
          {res.map((row,i)=>(
            <div className="res-row-card" key={row.id}>
              <div className="res-row-head"><span className="res-row-num">Resource #{i+1}</span>{res.length>1&&<button className="res-remove" onClick={()=>setRes(rs=>rs.filter(r=>r.id!==row.id))}>Remove</button>}</div>
              <div className="res-grid">
                <FField label="Role / Title" required><input className={`fctrl${!row.title.trim()?" err":""}`} placeholder="e.g. Senior Data Engineer" value={row.title} onChange={e=>upRes(row.id,"title",e.target.value)}/></FField>
                <FField label="Capabilities"><input className="fctrl" placeholder="e.g. Spark, dbt" value={row.capabilities} onChange={e=>upRes(row.id,"capabilities",e.target.value)}/></FField>
                <FField label={`Hourly Cost (${v.currency})`}><input className={`fctrl mono${!row.hourlyCost?" err":""}`} type="number" min="0" placeholder="0" value={row.hourlyCost} onChange={e=>upRes(row.id,"hourlyCost",e.target.value)}/></FField>
                <FField label="Qty"><input className={`fctrl mono${!row.quantity?" err":""}`} type="number" min="1" placeholder="1" value={row.quantity} onChange={e=>upRes(row.id,"quantity",e.target.value)}/></FField>
              </div>
              <FField label="Notes"><input className="fctrl" placeholder="Availability notes…" value={row.notes} onChange={e=>upRes(row.id,"notes",e.target.value)}/></FField>
            </div>
          ))}
        </div>
        <button className="add-res" onClick={()=>setRes(rs=>[...rs,emptyRes()])}>+ Add Resource Requirement</button>
      </div>
      <div className="form-actions"><button className="btn-pri" onClick={go} disabled={!can}>{sub?"Creating…":"Create Project"}</button><button className="btn-sec" onClick={onCancel}>Cancel</button><span style={{fontSize:11.5,color:C.dim,marginLeft:"auto"}}>Required fields <span style={{color:C.red}}>*</span></span></div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FINANCIAL STATUS — seed data + SVG charts
// ─────────────────────────────────────────────────────────────────────────────


